import React, { FC, useMemo } from 'react';

import { Tooltip } from 'antd';
import { observer } from 'mobx-react';

import i18n from '@/content';
import { useStores } from '@/components/hooks';
import { ApplyRulesResponse } from '@/api/requests';
import { UploadedItem } from '@/stores';

import styles from './index.module.scss';

const nameSpace = 'uploadFiles.successTable';

interface BlockedSummaryBarProps {
    rulesResults: ApplyRulesResponse;
}

const BlockedSummaryBar: FC<BlockedSummaryBarProps> = observer(({ rulesResults }) => {
    const { sharedUsersStore, uploadFilesStore } = useStores();
    const { recipientsList } = sharedUsersStore;
    const { successfullyUploadedItems } = uploadFilesStore;

    const { entirelyBlockedUserEmails, entirelyBlockedFiles } = useMemo(() => {
        // file_id -> list of blocked identities (emails + group ids, emails stored lowercase for matching)
        const map = new Map<string, string[]>();
        const blockRules = rulesResults.filter((r) => r.action === 'block');
        blockRules.forEach((rule) => {
            if (!map.has(rule.file_id)) {
                map.set(rule.file_id, []);
            }
            const list = map.get(rule.file_id)!;
            rule.recipients?.forEach((email) => {
                const normalized = email?.toLowerCase();
                if (normalized && !list.includes(normalized)) {
                    list.push(normalized);
                }
            });
            rule.groups?.forEach((groupId) => {
                if (groupId && !list.includes(groupId)) {
                    list.push(groupId);
                }
            });
        });

        const fileIds = successfullyUploadedItems.map((f) => f.fid).filter(Boolean) as string[];
        const recipientCount = recipientsList.length;

        // User is entirely blocked if blocked from every file (match by email case-insensitive)
        const entirelyBlockedUserEmails = recipientsList
            .filter((r) => r.type === 'user' && r.email)
            .filter((r) => {
                const identity = r.email!.toLowerCase();
                return fileIds.length > 0 && fileIds.every((fid) => {
                    const blocked = map.get(fid) ?? [];
                    return blocked.some((b) => b.toLowerCase() === identity);
                });
            })
            .map((r) => r.email!);

        // File is entirely blocked if every recipient (user or group) is in the blocked list for that file
        const entirelyBlockedFiles: UploadedItem[] = successfullyUploadedItems.filter((item) => {
            if (!item.fid || recipientCount === 0) return false;
            const blocked = map.get(item.fid) ?? [];
            const blockedRecipientCount = recipientsList.filter((r) => {
                const identity = r.type === 'user' ? r.email?.toLowerCase() : r.id;
                if (!identity) return false;
                if (r.type === 'user') {
                    return blocked.some((b) => b.toLowerCase() === identity);
                }
                return blocked.includes(identity);
            }).length;
            return blockedRecipientCount === recipientCount;
        });

        return { entirelyBlockedUserEmails, entirelyBlockedFiles };
    }, [rulesResults, recipientsList, successfullyUploadedItems]);

    const blockedUsersCount = entirelyBlockedUserEmails.length;
    const blockedFilesCount = entirelyBlockedFiles.length;

    if (blockedUsersCount === 0 && blockedFilesCount === 0) {
        return null;
    }

    const usersLabel =
        blockedUsersCount === 1
            ? i18n.t(`${nameSpace}.entirelyBlockedUser`)
            : i18n.t(`${nameSpace}.entirelyBlockedUsers`, { count: blockedUsersCount });

    const filesLabel =
        blockedFilesCount === 1
            ? i18n.t(`${nameSpace}.entirelyBlockedFile`)
            : i18n.t(`${nameSpace}.entirelyBlockedFiles`, { count: blockedFilesCount });

    const tooltipContent = (items: string[]) => (
        <div>
            {items.map((item) => (
                <div
                    key={item}
                    style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.6 }}
                >
                    {item}
                </div>
            ))}
        </div>
    );

    const blockedFileNames = entirelyBlockedFiles.map((f) => f.filename ?? '').filter(Boolean);

    return (
        <div className={styles.bar}>
            {blockedUsersCount > 0 && (
                <Tooltip
                    placement="top"
                    color="#091F56"
                    title={tooltipContent(entirelyBlockedUserEmails)}
                >
                    <span className={styles.section}>{usersLabel}</span>
                </Tooltip>
            )}
            {blockedFilesCount > 0 && (
                <Tooltip
                    placement="top"
                    color="#091F56"
                    title={tooltipContent(blockedFileNames)}
                >
                    <span className={styles.section}>{filesLabel}</span>
                </Tooltip>
            )}
        </div>
    );
});

export default BlockedSummaryBar;
